import { toast } from 'react-toastify';

/**
 * Handle authentication errors
 * - Show toast notification
 * - Save current location for post-login redirect
 * - Clear authentication data
 * - Redirect to login page
 */
const handleAuthError = () => {
  // Show toast notification
  toast.error('Your session has expired. Please login again.', {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  // Save current location for redirect after re-login
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath !== '/login') {
    localStorage.setItem('returnTo', currentPath);
  }

  // Clear all authentication data
  localStorage.removeItem('accessToken');
  localStorage.removeItem('chatUserToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('countryCodes');
  localStorage.removeItem('shardId');
  localStorage.removeItem('userFirstName');
  localStorage.removeItem('userLastName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userDepartment');
  localStorage.removeItem('isCollectionsManager');

  // Redirect to login with returnTo query parameter
  setTimeout(() => {
    window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`;
  }, 500); // Small delay to ensure toast is visible
};

/**
 * Core API request function
 * Handles authentication, headers, and error responses
 */
const apiRequest = async (url, options = {}) => {
  // Get access token
  const token = localStorage.getItem('accessToken');

  // Build request configuration
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    // Check for authentication/authorization errors
    if (response.status === 401 || response.status === 403) {
      const data = await response.json().catch(() => ({}));

      // Check if error message indicates token expiration
      if (data.error && (
        data.error.includes('Invalid or expired token') ||
        data.error.includes('Access token is required')
      )) {
        handleAuthError();
        throw new Error('Authentication failed');
      }
    }

    return response;
  } catch (error) {
    // Re-throw network errors or other errors
    if (error.message === 'Authentication failed') {
      throw error;
    }

    // Handle network errors
    console.error('API Request Error:', error);
    throw error;
  }
};

/**
 * API Service
 * Centralized service for all API calls with automatic auth handling
 */
const apiService = {
  /**
   * GET request
   */
  get: async (url, options = {}) => {
    return apiRequest(url, { ...options, method: 'GET' });
  },

  /**
   * POST request
   */
  post: async (url, data = null, options = {}) => {
    return apiRequest(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PUT request
   */
  put: async (url, data = null, options = {}) => {
    return apiRequest(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete: async (url, options = {}) => {
    return apiRequest(url, { ...options, method: 'DELETE' });
  },

  /**
   * PATCH request
   */
  patch: async (url, data = null, options = {}) => {
    return apiRequest(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },
};

export default apiService;
